import { use, useCallback, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Card, EmptyState } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import ExportForm, {
	getExportFormatExtension,
	type ExportFormat,
	type ExportStatus,
} from '~components/forms/exportForm';
import { generalInformation, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import { HeartbeatsDataGrid } from '~features/telemetry';
import { useSnackbar } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import { extendDevice } from '~interfaces/device';
import ExportService from '~services/exportService';
import { getService } from '~services/serviceFactory';

import DeviceActionsBar from '../components/deviceActionBar';
import DeviceContext from '../providers/deviceContext';

type DeviceTelemetryActions = 'export';

const exportService = getService(ExportService);

function triggerBlobDownload(blob: Blob, fileName: string) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

const DeviceTelemetryContainer = () => {
	const auth = useAuthorize();
	const { t } = useTranslation('entities');
	const [searchParams] = useSearchParams();
	const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();
	const deviceContext = use(DeviceContext);
	const deviceId = deviceContext.device?.id ?? '0';
	const organisationId = deviceContext.overwrittenClientId;
	const [userAction, setUserAction] = useState<DeviceTelemetryActions | null>(null);
	const [exportStatus, setExportStatus] = useState<ExportStatus>('selecting');
	const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null);
	const showConnectivityProfileSettingButton =
		extendDevice(deviceContext.device)?.showConnectivityProfileSettingButton ?? false;

	const isSuperAdmin = auth.authorize([UserRoleEnum.SuperAdmin]);
	const allowsExporting = useAuthorize().authorize([
		UserRoleEnum.SuperAdmin,
		UserRoleEnum.Support,
		UserRoleEnum.Manufacturing,
		UserRoleEnum.ClientAdmin,
	]);

	const clearUserAction = () => {
		// Close the dialog first; reset internal state after the exit animation
		setUserAction(null);
		setTimeout(() => {
			setExportStatus('selecting');
			setSelectedFormat(null);
		}, 300);
	};

	const handleExport = useCallback(
		async (format: ExportFormat) => {
			setExportStatus('exporting');

			const startDate =
				searchParams.get(queryParameters.StartDate) ??
				dayjs().add(-7, 'day').startOf('day').toISOString();
			const endDate =
				searchParams.get(queryParameters.EndDate) ?? dayjs().endOf('day').toISOString();

			const start = dayjs(startDate).toISOString();
			const end = dayjs(endDate).toISOString();

			try {
				const jobId = await exportService.exportMetrics({
					deviceId,
					start,
					end,
					format,
					overwrittenClientId: organisationId,
				});

				const blob = await exportService.downloadExport({ jobId });

				const fileName = `${dayjs(startDate).format('DD-MM-YYYY')}_${dayjs(endDate).format('DD-MM-YYYY')}_metrics_export${getExportFormatExtension(format)}`;
				triggerBlobDownload(blob, fileName);

				enqueueSuccessSnackbar(t('exportGeneratedSuccessfully', { ns: 'toast' }));
			} catch (error) {
				console.error('Export failed:', error);
				enqueueErrorSnackbar(t('exportFailedToGenerate', { ns: 'toast' }));
			} finally {
				clearUserAction();
			}
		},
		[deviceId, organisationId, searchParams, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]
	);

	return (
		<>
			<DeviceActionsBar
				deviceId={deviceId}
				organisationId={organisationId}
				showUnassignButton={
					isSuperAdmin &&
					organisationId != undefined &&
					organisationId != generalInformation.manufacturingOrganisationId
				}
				showAssignButton={
					isSuperAdmin && organisationId == generalInformation.manufacturingOrganisationId
				}
				showEditButton={isSuperAdmin}
				showChangeResourceGroupButton={
					isSuperAdmin && organisationId != generalInformation.manufacturingOrganisationId
				}
				showDeleteButton={isSuperAdmin}
				showConnectivityProfileSettingButton={showConnectivityProfileSettingButton}
				showRemoteActionsButton={isSuperAdmin}
			/>
			<Card title={t('device.fields.latestHeartbeats', { ns: 'entities' })} sx={{ height: '100%' }}>
				{deviceId != undefined ?
					<HeartbeatsDataGrid
						deviceId={deviceId}
						overwrittenClientId={organisationId}
						showExportButton={allowsExporting}
						initialStartDate={dayjs().add(-7, 'day').startOf('day')}
						initialEndDate={dayjs().endOf('day')}
						handleExportButtonClicked={() => setUserAction('export')}
					/>
				:	<EmptyState
						icon={'icon/bar-chart-square'}
						title={t('device.status.noHeartbeatsYet')}
						description='There is no telemetry data available for this device'
					/>
				}
			</Card>
			<FormDialog
				open={userAction === 'export'}
				title={
					t('export', { ns: 'actions' }) +
					' ' +
					t('metric.labels.plural', { ns: 'entities' }).toLowerCase()
				}
				loading={exportStatus === 'exporting'}
				submitLabel={t('export', { ns: 'actions' })}
			>
				<ExportForm
					status={exportStatus}
					selectedFormat={selectedFormat}
					onFormatChange={setSelectedFormat}
					onSubmit={handleExport}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default DeviceTelemetryContainer;
